import './popupUpsert.scss';

import React, { useEffect, useImperativeHandle, useRef, useCallback, useState } from "react";
import { confirm } from "devextreme/ui/dialog";
import { Form, Button, Popup, Toast, CheckBox, ScrollView, LoadPanel, DataGrid } from "devextreme-react";
import { DataGridRef, Column } from "devextreme-react/data-grid";
import { FormRef, Item } from "devextreme-react/form";
import { Position, ToolbarItem } from "devextreme-react/popup";
import useApiConfiguration from "../../../../apiConfig";
import { PersonaleRapportinoDTOFromJSON, PersonaleRapportinoDTOToJSON, RapportiniApi, RapportiniDTO, RapportiniDTOToJSON, RapportiniUpdateDTOFromJSON } from "../../../../data";
import { ATS_RapportoLavoroPersonaleEdit } from "..";
import { PopupPersonaleEditHandle } from "./popupPersonaleEdit";
import { ClickEvent } from "devextreme/ui/button";
import { useScreenSize } from "../../../../utils/media-query";

export type PopupEditHandle = {
    show: (id: number) => void
};

type Props = {
    onChange: () => void
};

type State = {
    id: number | undefined,
    visible: boolean
};

const PopupEdit: React.ForwardRefRenderFunction<PopupEditHandle, Props> = (props, ref) => {
    const [loading, setLoading] = useState(false);
    const [state, setState] = useState<State>({ id: undefined, visible: false });
    const [formData, setFormData] = useState<RapportiniDTO>();
    const [toastConfig, setToastConfig] = useState({ isVisible: false, message: '' } as { isVisible: boolean, message: string });

    const formRef = useRef<FormRef>(null);
    const gridRef = useRef<DataGridRef>(null);
    const popupPersonaleEditRef = useRef<PopupPersonaleEditHandle>(null);
    const apiConfig = useApiConfiguration();
    const { isLarge, isMedium, isSmall, isXSmall } = useScreenSize();

    function hideInfo() {
        setLoading(false);
        setState({ id: undefined, visible: false });
    }

    useImperativeHandle(ref, () => ({
        show(id) {
            setLoading(true);
            setState({ id: id, visible: true });
        }
    }));

    useEffect(() => {
        if (state.id) {
            const api = new RapportiniApi(apiConfig);
            api.getRapportinoById({ idRapportino: state.id })
                .then(function (data) {
                    if (data) {
                        setFormData({
                            ...data,
                            id: state.id
                        });

                        setLoading(false);
                    }
                })
                .catch(() => hideInfo())
        }
    }, [state.id, state.visible]);

    const onHidingToast = useCallback(() => {
        setToastConfig({
            ...toastConfig,
            isVisible: false
        });
    }, [toastConfig, setToastConfig]);

    const update = (e: ClickEvent) => {
        const validate = formRef.current?.instance().validate();

        if (validate?.isValid) {
            if ((formData.personaleRapportino ?? []).length == 0) {
                setToastConfig({
                    ...toastConfig,
                    isVisible: true,
                    message: 'Inserire il personale'
                });
            } else {
                var json = RapportiniDTOToJSON(formData);
                var objUpd = RapportiniUpdateDTOFromJSON(json);

                var api = new RapportiniApi(apiConfig);
                api.updateRapportinoById({
                    id: state.id,
                    rapportiniUpdateDTO: objUpd
                })
                    .then(() => props.onChange())
                    .finally(() => hideInfo());
            }
        }
    };

    const openPersonale = (rowIdx: number) => {
        if (rowIdx == -1) {
            if (formData.personaleRapportino.length > 0) {
                var obj = formData.personaleRapportino.find((_, idx) => idx === 0);
                var json = JSON.stringify(PersonaleRapportinoDTOToJSON(obj));
                popupPersonaleEditRef.current.show(json, formData.idCommessa, formData.dataRapportino, false);
            } else
                popupPersonaleEditRef.current.show('', formData.idCommessa, formData.dataRapportino, false);
        } else {
            var obj = formData.personaleRapportino.find((_, idx) => idx === rowIdx);
            var json = JSON.stringify(PersonaleRapportinoDTOToJSON(obj));
            popupPersonaleEditRef.current.show(json, formData.idCommessa, formData.dataRapportino, true);
        }
    }

    const upsertPersonale = (json: string) => {
        const objUpd = PersonaleRapportinoDTOFromJSON(JSON.parse(json));
        const list = formData.personaleRapportino ?? [];
        let updList;

        const exists = list.some(item => item.idPersonale === objUpd.idPersonale);
        if (exists)
            updList = list.map(item => item.idPersonale === objUpd.idPersonale ? objUpd : item);
        else
            updList = [...list, objUpd];

        setFormData((prev) => ({
            ...prev,
            personaleRapportino: updList
        }));
    }

    function checkDeletePersonale(rowIndex: number) {
        const result = confirm("Confermi l'eliminazione?", "Conferma");
        result.then((dialogRes) => {
            if (dialogRes)
                deletePersonale(rowIndex);
        });
    }

    function deletePersonale(rowIndex: number) {
        setFormData((prev) => ({
            ...prev,
            personaleRapportino: prev.personaleRapportino.filter((_, idx) => idx !== rowIndex)
        }));

        gridRef.current?.instance().refresh();
    }

    return (
        <React.Fragment>
            <Popup
                visible={state.visible}
                dragEnabled={false}
                hideOnOutsideClick={true}
                showCloseButton={true}
                fullScreen={(isSmall || isXSmall ? true : false)}
                showTitle={true}
                title={"Modifica Rapporto M. 25"}
                container=".dx-viewport"
                height={"85%"}
                onHidden={hideInfo}
            >
                <Position at="center" my="center" of="" collision="fit" />

                <ToolbarItem widget="dxButton" toolbar="bottom" location="center">
                    <Button
                        onClick={update}
                        icon="check"
                        text="Salva"
                        type="default"
                        stylingMode="contained"
                    />
                </ToolbarItem>

                {
                    loading ?
                        <LoadPanel style={{ zIndex: 9999 }} shading={true} shadingColor="rgba(0, 0, 0, 0.4)" position={'center'} visible={true} />
                        : (
                            <ScrollView className="rapportino-edit-scroll">
                                <Form
                                    ref={formRef} formData={formData}
                                    labelMode="floating" labelLocation="left"
                                    colCount={(isLarge || isMedium) ? 4 : 2}
                                    style={{ padding: "10px" }}
                                >
                                    <Item visible={false} dataField="id" />
                                    <Item visible={false} dataField="tipoRapportino" />

                                    <Item colSpan={(isLarge || isMedium) ? 1 : 2} dataField="dataRapportino" label={{ text: "Data" }}
                                        editorType="dxDateBox"
                                        editorOptions={{
                                            disabled: true,
                                            displayFormat: (isLarge || isMedium) ? "EEEE d MMMM yyyy" : "dd/MM/yyyy"
                                        }}
                                    />

                                    <Item colSpan={3} visible={(isLarge || isMedium)} itemType="empty" />

                                    <Item colSpan={2} dataField="cliente" editorType="dxTextBox" editorOptions={{ disabled: true }} />

                                    <Item colSpan={2} dataField="commessa" editorType="dxTextBox" editorOptions={{ disabled: true }} />

                                    <Item colSpan={2} dataField="descrizioneAttivita" label={{ text: "Descrizione Attività" }} editorType="dxTextBox" isRequired={true} />

                                    <Item colSpan={2} dataField="ubicazioneTest" label={{ text: "Ubicazione Test / Attività" }} editorType="dxTextBox" isRequired={true} />

                                    <Item colSpan={(isLarge || isMedium) ? 4 : 2} dataField="descrizioneLavori"
                                        editorType="dxTextArea" isRequired={true} cssClass="dx-textarea-custom"
                                        editorOptions={{
                                            maxLength: 1500,
                                            autoResizeEnabled: true
                                        }}
                                    />

                                    <Item colSpan={2} dataField="strumentiMisura" label={{ text: "Strumenti di Misura utilizzati" }}
                                        editorType="dxTextArea" cssClass="dx-textarea-custom"
                                        editorOptions={{
                                            maxLength: 250,
                                            autoResizeEnabled: true
                                        }}
                                    />

                                    <Item colSpan={2}>
                                        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                                            <CheckBox
                                                defaultValue={formData?.isRelazioneProva}
                                                onValueChanged={(e) => formData.isRelazioneProva = e.value}
                                                text="Relazione di prova"
                                            />
                                            <CheckBox
                                                defaultValue={formData?.isSchedaTecnica}
                                                onValueChanged={(e) => formData.isSchedaTecnica = e.value}
                                                text="Scheda tecnica"
                                            />
                                        </div>
                                    </Item>

                                    <Item colSpan={(isLarge || isMedium) ? 4 : 2}>
                                        <div style={{ display: "flex", justifyContent: "end", marginTop: "10px", marginBottom: "10px" }}>
                                            <Button icon="add" text="Aggiungi Personale" stylingMode="text" onClick={() => openPersonale(-1)} />
                                        </div>
                                        <DataGrid
                                            className="grid theme-dependent" ref={gridRef}
                                            dataSource={formData?.personaleRapportino}
                                            showBorders={false} rowAlternationEnabled={true}
                                            defaultFocusedRowIndex={0} noDataText="Nessun personale caricato"
                                        >
                                            <Column
                                                width={100}
                                                cellRender={(cellData) => (
                                                    <div style={{ display: "flex" }}>
                                                        <Button icon="edit" hint="Modifica" stylingMode="text" onClick={() => openPersonale(cellData.rowIndex)} />
                                                        <Button icon="trash" hint="Elimina" stylingMode="text" onClick={() => checkDeletePersonale(cellData.rowIndex)} />
                                                    </div>
                                                )}
                                            />
                                            <Column dataField="personale" />
                                            <Column dataField="oreTotali" />
                                            <Column dataField="oreViaggio" />
                                            <Column dataField="viaggio" caption="Viaggi (KM)" />
                                            <Column dataField="pernottamenti" />
                                        </DataGrid>
                                    </Item>
                                </Form>
                            </ScrollView>
                        )
                }
            </Popup>

            <Toast
                visible={toastConfig.isVisible}
                message={toastConfig.message}
                height={45} width={200}
                position={{ at: 'center', my: 'center', of: window, offset: { x: 0, y: 0 } }}
                type='error' displayTime={3000}
                onHiding={onHidingToast}
                animation={{ show: { type: 'fade', duration: 400, from: 0, to: 1 }, hide: { type: 'fade', duration: 40, to: 0 } }}
            />

            <ATS_RapportoLavoroPersonaleEdit ref={popupPersonaleEditRef} onChange={upsertPersonale} />
        </React.Fragment>
    )
}

export default React.forwardRef(PopupEdit);